// Grouping + ordering rules for the Direct Debit run selector. Kept out of the
// component so the ordering (which was previously day-of-month only) is unit
// testable. Mirrors DirectDebitRunStatus on the API side.
import { DirectDebitRun } from '../../model/payments/DirectDebitRun';
import type { TagTone } from '../../ui/primitives/Tag';

export type DirectDebitRunGroupKey = 'needsAttention' | 'pending' | 'completed';

export interface DirectDebitRunGroup {
    key: DirectDebitRunGroupKey;
    label: string;
    runs: DirectDebitRun[];
}

// Runs that still need an operator to act on them sort above the ones that
// don't, and the most recent payment date comes first within each group.
const GROUP_ORDER: { key: DirectDebitRunGroupKey; label: string }[] = [
    { key: 'needsAttention', label: 'Needs attention' },
    { key: 'pending', label: 'Pending' },
    { key: 'completed', label: 'Completed' },
];

export const getRunGroupKey = (status: string): DirectDebitRunGroupKey => {
    switch (status) {
        case 'Failed':
            return 'needsAttention';
        case 'Completed':
            return 'completed';
        default:
            // Created / Submitted / RejectionsProcessed all still await an
            // operator action, so they read as pending — as does any status
            // this doesn't know, which is the safer place to put one.
            return 'pending';
    }
};

// Statuses arrive as DirectDebitRunStatus enum names (JsonStringEnumConverter),
// so compound ones like RejectionsProcessed need splitting. Done generically so
// a new enum member reads correctly without a code change here.
export const getRunStatusLabel = (status: string): string => {
    // The model types status as required, but nothing validates the payload at
    // runtime — a missing one shouldn't take the whole run manager down over a
    // cosmetic pill.
    if (!status) return '';
    const words = status.replace(/([a-z0-9])([A-Z])/g, '$1 $2').split(' ');
    return words
        .map((word, index) => (index === 0 ? word : word.toLowerCase()))
        .join(' ');
};

// Tone carries urgency class, not lifecycle position — the label already says
// which status it is. Amber = an operator still has to do something, blue = in
// flight at the bank, green = done, red = failed, grey = a status we don't know.
//
// Deliberately no `primary`: that tone resolves to the tenant's branding colour
// (buildTokens folds `branding.primaryColor` onto `tokens.color.primary`), so a
// status pill using it would change meaning per club and could come out green
// or red beside the pills that mean exactly that.
export const getRunStatusTone = (status: string): TagTone => {
    switch (status) {
        case 'Completed':
            return 'success';
        case 'Failed':
            return 'danger';
        case 'Submitted':
            return 'processing';
        case 'Created':
        case 'RejectionsProcessed':
            // Both still await an operator: submit the file, or complete the run.
            return 'warning';
        default:
            return 'default';
    }
};

// Most recent payment date first. The previous implementation compared
// `getDate()` (day of month), so runs from different months/years interleaved.
const byPaymentDateDescending = (a: DirectDebitRun, b: DirectDebitRun): number =>
    new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime();

export const groupDirectDebitRuns = (runs: DirectDebitRun[]): DirectDebitRunGroup[] =>
    GROUP_ORDER.map(({ key, label }) => ({
        key,
        label,
        runs: runs.filter(run => getRunGroupKey(run.status) === key).sort(byPaymentDateDescending),
    })).filter(group => group.runs.length > 0);
